import { useCallback, useEffect, useRef, useState } from 'react';
import { RANKS, rankFor } from '@aleaces/engine';
import type { GameMode, MatchResult, ServerMessage, TableSnapshot } from '@aleaces/protocol';
import { OnboardingModal } from './components/OnboardingModal';
import { Toasts } from './components/Toasts';
import { TopBar, type MenuScreenId } from './components/TopBar';
import { useProfile } from './hooks';
import { setMusicScene } from './music';
import { net } from './net';
import { SFX } from './sfx';
import { CollectionScreen } from './screens/CollectionScreen';
import { HeroDrawScreen } from './screens/HeroDrawScreen';
import { MatchScreen } from './screens/MatchScreen';
import { MenuScreen } from './screens/MenuScreen';
import { PassScreen } from './screens/PassScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { QueueScreen } from './screens/QueueScreen';
import { supabase } from './supabase';
import { toast } from './toast';

/** Phones play in landscape only; portrait shows this hint (visibility via CSS). */
function RotateHint() {
  return (
    <div className="rotate-overlay" role="status">
      <div className="phone" aria-hidden="true" />
      <h2>Bitte Gerät drehen</h2>
      <p>Ale &amp; Aces spielt sich im Querformat – dreh dein Gerät, dann geht es weiter.</p>
    </div>
  );
}

type Flow =
  | { stage: 'queue'; mode: GameMode; inQueue: number; needed: number; secondsLeft: number }
  | { stage: 'draw'; mode: GameMode; heroId: string; rerollsLeft: number; deadline: number; players: string[] }
  | { stage: 'match'; mode: GameMode }
  | null;

export function App() {
  const profile = useProfile();
  const [screen, setScreen] = useState<MenuScreenId>('menu');
  const [flow, setFlow] = useState<Flow>(null);
  const [snapshot, setSnapshot] = useState<TableSnapshot | null>(null);
  const [result, setResult] = useState<MatchResult | null>(null);
  const [connError, setConnError] = useState(false);
  const flowRef = useRef<Flow>(null);
  flowRef.current = flow;

  // connect & login on startup; the server sends the profile back
  useEffect(() => {
    net.connect().catch(() => setConnError(true));
  }, []);

  // calm tavern music in the menus, a quieter tune at the table
  const atTable = flow?.stage === 'match';
  useEffect(() => {
    setMusicScene(atTable ? 'table' : 'tavern');
  }, [atTable]);

  // after a Supabase sign-in/out, reconnect so hello carries the new identity
  useEffect(() => {
    if (!supabase) return;
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        net.disconnect();
        net.connect().catch(() => setConnError(true));
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    return net.onMessage((msg: ServerMessage) => {
      switch (msg.type) {
        case 'queueUpdate': {
          const prev = flowRef.current;
          if (prev?.stage === 'queue' && msg.inQueue > prev.inQueue) SFX.queueSeat();
          setFlow((f) => (f?.stage === 'queue' ? { ...f, inQueue: msg.inQueue, needed: msg.needed, secondsLeft: msg.secondsLeft } : f));
          break;
        }
        case 'queueCancelled':
          setFlow((f) => (f?.stage === 'queue' ? null : f));
          break;
        case 'matchFound': {
          SFX.matchFound();
          setResult(null);
          setFlow({
            stage: 'draw',
            mode: msg.mode,
            heroId: msg.heroId,
            rerollsLeft: msg.rerollsLeft,
            deadline: Date.now() + msg.chooseSeconds * 1000,
            players: msg.players.map((p) => p.name),
          });
          break;
        }
        case 'heroUpdate':
          setFlow((f) => (f?.stage === 'draw' ? { ...f, heroId: msg.heroId, rerollsLeft: msg.rerollsLeft } : f));
          break;
        case 'matchStart':
          setSnapshot(msg.snapshot);
          setResult(null);
          setFlow((f) => ({ stage: 'match', mode: f && 'mode' in f ? f.mode : msg.snapshot.mode }));
          break;
        case 'state':
          setSnapshot(msg.snapshot);
          break;
        case 'result':
          setResult(msg.result);
          break;
        case 'tableClosed':
          setFlow((f) => (f?.stage === 'match' || f?.stage === 'draw' ? null : f));
          setSnapshot(null);
          break;
        case 'error':
          if (flowRef.current) {
            toast('Verbindungsproblem', msg.message);
            setFlow(null);
            setSnapshot(null);
          }
          break;
        default:
          break;
      }
    });
  }, []);

  const startQueue = useCallback((mode: GameMode) => {
    net
      .connect()
      .then(() => {
        net.send({ type: 'queue', mode });
        SFX.queueStart();
        setFlow({ stage: 'queue', mode, inQueue: 1, needed: mode === 'ranked' ? 6 : 3, secondsLeft: 12 });
      })
      .catch(() => toast('Server nicht erreichbar', 'Läuft der Spielserver? (pnpm dev)'));
  }, []);

  const cancelQueue = useCallback(() => {
    net.send({ type: 'cancelQueue' });
    setFlow(null);
  }, []);

  const leaveToMenu = useCallback(() => {
    net.send({ type: 'leaveTable' });
    setFlow(null);
    setSnapshot(null);
    setResult(null);
    setScreen('menu');
  }, []);

  const playAgain = useCallback((mode: GameMode) => {
    net.send({ type: 'leaveTable' });
    setFlow(null);
    setSnapshot(null);
    setResult(null);
    startQueue(mode);
  }, [startQueue]);

  if (!profile) {
    return (
      <div className="app">
        <RotateHint />
        <div className="overlay-screen">
          <div className="logo" style={{ fontSize: 40 }}>Ale &amp; Aces<small>Tavernen-Hold'em</small></div>
          {connError ? (
            <>
              <p className="muted" style={{ fontFamily: 'var(--font-ui)' }}>Kein Kontakt zum Spielserver.</p>
              <button className="btn gold" onClick={() => { setConnError(false); net.connect().catch(() => setConnError(true)); }}>
                Erneut verbinden
              </button>
            </>
          ) : (
            <p className="muted" style={{ fontFamily: 'var(--font-ui)' }}>Verbinde mit der Taverne …</p>
          )}
        </div>
        <Toasts />
      </div>
    );
  }

  const inIsolatedFlow = flow !== null;

  return (
    <div className="app">
      <RotateHint />
      {!inIsolatedFlow && (
        <>
          <TopBar screen={screen} onNavigate={setScreen} />
          <main>
            {screen === 'menu' && <MenuScreen onPlay={startQueue} />}
            {screen === 'pass' && <PassScreen />}
            {screen === 'collection' && <CollectionScreen />}
            {screen === 'profile' && <ProfileScreen />}
          </main>
          <OnboardingModal />
        </>
      )}
      {flow?.stage === 'queue' && (
        <QueueScreen mode={flow.mode} inQueue={flow.inQueue} needed={flow.needed} secondsLeft={flow.secondsLeft} onCancel={cancelQueue} />
      )}
      {flow?.stage === 'draw' && (
        <HeroDrawScreen
          mode={flow.mode}
          heroId={flow.heroId}
          rerollsLeft={flow.rerollsLeft}
          deadline={flow.deadline}
          players={flow.players}
          onReroll={() => net.send({ type: 'reroll' })}
          onReady={() => net.send({ type: 'ready' })}
        />
      )}
      {flow?.stage === 'match' && snapshot && (
        <MatchScreen snapshot={snapshot} onLeave={leaveToMenu} />
      )}
      {result && <ResultModal result={result} onAgain={() => playAgain(result.mode)} onMenu={leaveToMenu} />}
      <Toasts />
    </div>
  );
}

function ResultModal({ result, onAgain, onMenu }: { result: MatchResult; onAgain: () => void; onMenu: () => void }) {
  const rk = rankFor(result.newRating);
  // the old rating is derivable, so promotion/demotion needs no protocol change
  const oldRk = rankFor(result.newRating - result.ratingDelta);
  const rankChange: 'up' | 'down' | null =
    result.mode !== 'ranked' || oldRk.id === rk.id ? null : RANKS.indexOf(rk) > RANKS.indexOf(oldRk) ? 'up' : 'down';
  useEffect(() => {
    if (rankChange === 'up') SFX.levelup();
    else if (rankChange === 'down') SFX.lose();
  }, [rankChange]);
  const ordinal =
    ({ 1: 'Sieg in der Taverne!', 2: 'Knapp am Sieg vorbei.', 3: 'Ein Platz am Ehrentisch.' } as Record<number, string>)[result.placement] ??
    'Nächste Runde geht auf dich.';
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="result-title">
      <div className="modal-box">
        <div className="eyebrow">{result.mode === 'ranked' ? 'Ranglistenturnier' : 'Freundschaftsrunde'} beendet</div>
        <div className="result-place" aria-hidden="true">{result.placement}.</div>
        <h2 id="result-title">{ordinal}</h2>
        {rankChange && (
          <div className={'rank-change ' + rankChange} role="status">
            <div className="rc-shields" aria-hidden="true">
              <div className="rank-shield rc-old" style={{ background: oldRk.color }}>{oldRk.name[0]}</div>
              <span className="rc-arrow">{rankChange === 'up' ? '▲' : '▼'}</span>
              <div className="rank-shield rc-new" style={{ background: rk.color }}>{rk.name[0]}</div>
            </div>
            <div className="rc-text">
              {rankChange === 'up' ? <>Aufstieg! Du bist jetzt <b>{rk.name}</b>.</> : <>Abstieg – zurück zu <b>{rk.name}</b>.</>}
            </div>
          </div>
        )}
        <div className="result-grid">
          <span>Platzierung</span>
          <span><b>{result.placement}. von {result.tableSize}</b></span>
          <span className="pos">Erfahrung</span>
          <span className="pos"><b>+{result.xp} EP</b></span>
          <span className="pos">Taler</span>
          <span className="pos"><b>+{result.taler}</b></span>
          {result.mode === 'ranked' && (
            <>
              <span className={result.ratingDelta >= 0 ? 'pos' : 'neg'}>Wertung</span>
              <span className={result.ratingDelta >= 0 ? 'pos' : 'neg'}>
                <b>{(result.ratingDelta >= 0 ? '+' : '') + result.ratingDelta} → {result.newRating} ({rk.name})</b>
              </span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn gold" autoFocus onClick={onAgain}>Noch ein Turnier</button>
          <button className="btn ghost" onClick={onMenu}>Zurück in die Taverne</button>
        </div>
      </div>
    </div>
  );
}
